
import React, { useEffect } from 'react';

import { RouteComponentProps, withRouter, Route, Redirect } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { Location } from 'history';

import { Routes } from '@share/constants';
import { IUser, properties } from '@share/common-types';
import { RootState, LoginType } from '@share/utils';
import { ILoginState, setLoginAccount, setLoginOriginalAccount, setLoginUser, setLoginOriginalUser, setLoginRedirect } from '@share/store/slices';
import { IAccount } from '@share/common-types';
import { NULL_VALUE } from '@constants';
import { isEmpty } from 'lodash';

interface ILoginRedirectProps {
  pathname: string;
  location: Location;
}

/**
 * Remembers the requested url, query string included, so that after signing in the member
 * lands back on it. Deep links carrying data in the query string (the insurance cross-sell
 * prefill, for instance) would otherwise lose it on the way through the sign in page.
 */
function LoginRedirect(props: ILoginRedirectProps) {
  const { pathname, location } = props;

  const dispatch = useDispatch();

  const redirect = `${location.pathname}${isEmpty(location.search) ? '' : location.search}`;

  useEffect(() => {
    dispatch(setLoginRedirect(redirect));
  }, [dispatch, redirect]);

  return (
    <Redirect
      to={{
        pathname,
        state: { from: location },
      }}
    />
  );
}

interface IMapStateToProps {
  loginStore: ILoginState;
}

interface IMapDispatchToProps {
  setLoginAccount: (redirect: IAccount) => void;
  setLoginOriginalAccount: (redirect: IAccount) => void;
  setLoginUser: (redirect: IUser) => void;
  setLoginOriginalUser: (redirect: IUser) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  children: any;
  path: string;
  exact?: boolean;
  isSignIn?: boolean;
}

class PrivateHotelRouteComponent extends React.Component<IProps> {

  componentDidMount() {
    const { account } = this.props.loginStore;
    if (account) {
      const validProperties = [properties[0].value, properties[2].value];
      if (!validProperties.includes(account.property)) {
        this.props.setLoginAccount(NULL_VALUE);
        this.props.setLoginOriginalAccount(NULL_VALUE);
        this.props.setLoginUser(NULL_VALUE);
        this.props.setLoginOriginalUser(NULL_VALUE);

        this.props.history.push({
          pathname: Routes.CustomError,
          state: {
            data: 'UNAUTHORIZED',
          },
        });
      }
    }
  }
  
  isAuthenticated = () => {
    if (this.props.isSignIn) {
      const { account } = this.props.loginStore;
      return !!account && account.type === LoginType.PrivateWithLogin;
    } else {
      const { account, user, loading, accessToken } = this.props.loginStore;

      const isPrivateWithLogin = account?.type === LoginType.PrivateWithLogin;
      const isPrivateWithToken = account?.type === LoginType.PrivateWithToken;
      const isPublic = account?.type === LoginType.Public;
      return isPublic || !!user || (isPrivateWithLogin && !isEmpty(accessToken) && account?.allowLoginTokenPrivateWithLogin) || (isPrivateWithToken && loading);
    }
  }

  render(): React.ReactNode {
    const { account, loading, widgetRedirect } = this.props.loginStore;

    if (!account) {
      return null;
    }
    
    const isPrivateWithLogin = account?.type === LoginType.PrivateWithLogin;

    const widgetLogoutRedirect = account?.widgetLogoutRedirect;
    const disableSignInRedirect = account?.disableSignInRedirect;
    const isDisableSignIn = account?.isDisableSignIn;

    const pathname = this.props.isSignIn ? 
                          Routes.NotFound :
                          `/${account.name}${Routes.Login}`;
    const { children, exact, ...rest } = this.props;
    const exactProp = exact? exact : false;

    if (loading) {
      return null;
    }
        
    const isAuthenticated = this.isAuthenticated();
    const widgetRedirectStore = widgetRedirect;
    const widgetRedirectUrl = widgetLogoutRedirect;
    const widgetRedirectFinal = widgetRedirectStore || widgetRedirectUrl;

    if (!isAuthenticated && isPrivateWithLogin) {
      if (widgetRedirectFinal && !isEmpty(widgetRedirectFinal)) {
        window.location.replace(widgetRedirectFinal);
        return null;
      }
      
      if (isDisableSignIn && disableSignInRedirect && !isEmpty(disableSignInRedirect)) {
        window.location.replace(disableSignInRedirect);
        return null;
      }
    }

    return (
      <Route
        {...rest}
        exact={exactProp}
        render={
          ({ location }) => (
            isAuthenticated ?
              (children) :
              (<LoginRedirect pathname={pathname} location={location} />))
        }
      />
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setLoginAccount,
  setLoginOriginalAccount,
  setLoginUser,
  setLoginOriginalUser
};

export const PrivateRoute = connect(mapStateToProps, mapDispatchToProps)(withRouter(PrivateHotelRouteComponent));
